// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import styled, { css } from 'styled-components';
import RouterLink from 'next/link';

export const CardLink = styled(RouterLink)`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: 12px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.grafanaOrange};
    outline-offset: 2px;
  }
`;

export const ImageWrap = styled.div`
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const Image = styled.div<{ $src: string }>`
  width: 100%;
  aspect-ratio: 4 / 3;
  background: ${({ theme }) => theme.colors.white} url(${({ $src }) => $src}) no-repeat center;
  background-size: contain;
  transition: transform 0.25s ease;
`;

export const Badge = styled.span<{ $variant: string }>`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  ${({ $variant, theme }) =>
    ($variant === 'Bestseller' &&
      css`
        color: ${theme.colors.white};
        background: ${theme.colors.ink};
      `) ||
    ($variant === 'Sale' &&
      css`
        color: ${theme.colors.white};
        background: #d6336c;
      `) ||
    ($variant === 'Staff pick' &&
      css`
        color: ${theme.colors.white};
        background: ${theme.colors.otelBlue};
      `) ||
    ($variant === 'Low stock' &&
      css`
        color: ${theme.colors.ink};
        background: ${theme.colors.otelYellow};
      `) ||
    css`
      color: ${theme.colors.ink};
      background: linear-gradient(93deg, #f46800 0%, #ff8833 100%);
    `};
`;

export const QuickAdd = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 0.18s ease, transform 0.18s ease, background 0.18s ease;

  svg {
    stroke: ${({ theme }) => theme.colors.white};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.grafanaOrange};
    color: ${({ theme }) => theme.colors.ink};

    svg {
      stroke: ${({ theme }) => theme.colors.ink};
    }
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.grafanaOrange};
    color: ${({ theme }) => theme.colors.ink};
    cursor: default;
  }

  &:focus-visible {
    opacity: 1;
    transform: translateY(0);
  }

  @media (hover: none) {
    opacity: 1;
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none;
  }
`;

export const ProductCard = styled.div`
  position: relative;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.grafanaOrange};
    box-shadow: 0 8px 24px rgba(17, 18, 23, 0.08);

    ${QuickAdd} {
      opacity: 1;
      transform: translateY(0);
    }

    ${Image} {
      transform: scale(1.04);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;

      ${Image} {
        transform: none;
      }
    }
  }
`;

export const Content = styled.div`
  padding: 14px 16px 16px;
`;

export const Category = styled.p`
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.inkMuted};
`;

export const ProductName = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.ink};
`;

export const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;

  > span {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.inkMuted};
  }
`;

export const Stars = styled.span`
  display: inline-flex;
  letter-spacing: 1px;
`;

export const Star = styled.span<{ $filled: boolean }>`
  font-size: 13px;
  color: ${({ theme, $filled }) => ($filled ? theme.colors.grafanaOrange : theme.colors.border)};
`;

export const ProductPrice = styled.p`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 6px 0 0;
  font-size: ${({ theme }) => theme.sizes.dMedium};
  font-weight: ${({ theme }) => theme.fonts.semiBold};
  color: ${({ theme }) => theme.colors.ink};

  s {
    font-size: 13px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.inkMuted};
  }
`;
